import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useSearchParams } from "react-router-dom";
import ProductCard from "../../components/product/ProductCard";
import PageSeo from "../../components/common/PageSeo";
import styles from "../../styles/pages/search/SearchResultsPage.module.css";
import ProductSkeleton from "../../components/product/ProductSkeleton";
import api from "../../utils/api/axiosInstance";
import AppFeedbackPrompt from "../../components/feedback/AppFeedbackPrompt";
import { readHomeChatMatchSessionId } from "../../utils/homeChatMatchPersistence";
import { useUserAuth } from "../../contexts/UserAuthContext";
import useHomeChatMatchSession from "../../hooks/useHomeChatMatchSession";
import useProductGridMatchScores from "../../hooks/useProductGridMatchScores";
import {
  buildSearchFitIntent,
  shouldShowGridMatchScores,
} from "../../utils/gridFitIntentUtils";
import {
  FaThLarge,
  FaList,
  FaArrowDown,
  FaArrowUp,
  FaFilter,
  FaTimes,
  FaStar,
} from "react-icons/fa";

const SORT_OPTIONS = [
  {
    text: "Price: Low to High",
    label: (
      <>
        <FaArrowDown size={20} className={styles.sortIcon} />
        Price: Low to High
      </>
    ),
    value: "price-asc",
  },
  {
    text: "Price: High to Low",
    label: (
      <>
        <FaArrowUp size={20} className={styles.sortIcon} />
        Price: High to Low
      </>
    ),
    value: "price-desc",
  },
  { text: "Name: A-Z", label: "Name: A-Z", value: "name-asc" },
  { text: "Name: Z-A", label: "Name: Z-A", value: "name-desc" },
];

const SearchResultsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useUserAuth();
  const aiSession = useHomeChatMatchSession(user);
  const query = searchParams.get("q") || "";
  const categoryParam = searchParams.get("category") || "All";
  const categoryIdParam = searchParams.get("categoryId") || null;
  const sortByParam = searchParams.get("sortBy") || "";
  const limit = 20;

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [viewMode, setViewMode] = useState(
    localStorage.getItem("gridViewMode") || "grid",
  );
  const [sortBy, setSortBy] = useState(sortByParam || "");
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const skipRef = useRef(0);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const [advancedFilters, setAdvancedFilters] = useState({
    minPrice: "",
    maxPrice: "",
    brand: "",
    minRating: "",
  });
  const [pendingFilters, setPendingFilters] = useState({
    minPrice: "",
    maxPrice: "",
    brand: "",
    minRating: "",
  });
  const [brands, setBrands] = useState([]);
  const [maxPrice, setMaxPrice] = useState(10000);

  useEffect(() => {
    localStorage.removeItem("wizardResultsActive");
  }, []);

  useEffect(() => {
    localStorage.setItem("gridViewMode", viewMode);
  }, [viewMode]);

  useEffect(() => {
    skipRef.current = skip;
  }, [skip]);

  const fetchSearchResults = useCallback(
    async (resetSkip = true) => {
      if (!query.trim()) {
        setProducts([]);
        setTotal(0);
        setHasMore(false);
        setSkip(0);
        setLoading(false);
        return;
      }

      setLoading(true);
      setLoadError(null);
      try {
        const params = new URLSearchParams({
          q: query,
          limit,
          skip: resetSkip ? 0 : skipRef.current,
        });

        if (categoryIdParam) {
          params.append("categoryId", categoryIdParam);
        } else if (categoryParam && categoryParam !== "All") {
          params.append("category", categoryParam);
        }

        if (sortBy) params.append("sortBy", sortBy);
        if (advancedFilters.minPrice)
          params.append("minPrice", advancedFilters.minPrice);
        if (advancedFilters.maxPrice)
          params.append("maxPrice", advancedFilters.maxPrice);
        if (advancedFilters.brand)
          params.append("brand", advancedFilters.brand);
        if (advancedFilters.minRating)
          params.append("minRating", advancedFilters.minRating);

        const { data } = await api.get(
          `/products/search/full?${params.toString()}`,
        );
        const nextProducts = data.products || [];

        if (resetSkip) {
          setProducts(nextProducts);
          setSkip(nextProducts.length);
        } else {
          setProducts((prev) => [...prev, ...nextProducts]);
          setSkip((prev) => prev + nextProducts.length);
        }

        setTotal(data.total || 0);
        setHasMore(data.hasMore || false);
      } catch (err) {
        console.error("Error fetching search results:", err);
        setProducts([]);
        setLoadError("We couldn't load search results. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [query, categoryParam, categoryIdParam, sortBy, advancedFilters],
  );

  useEffect(() => {
    api
      .get("/brands")
      .then((res) => setBrands(res.data || []))
      .catch((err) => console.error("Failed to fetch brands:", err));
  }, []);

  useEffect(() => {
    if (!categoryIdParam) {
      setMaxPrice(10000);
      return;
    }
    api
      .get(`/products/max-price/${categoryIdParam}`)
      .then((res) => {
        const max = Number(res.data.maxPrice) || 10000;
        setMaxPrice(Math.ceil(max / 1000) * 1000);
      })
      .catch(() => setMaxPrice(10000));
  }, [categoryIdParam]);

  useEffect(() => {
    fetchSearchResults(true);
  }, [
    query,
    categoryParam,
    categoryIdParam,
    sortBy,
    advancedFilters,
    fetchSearchResults,
  ]);

  const handleShowMore = useCallback(() => {
    fetchSearchResults(false);
  }, [fetchSearchResults]);

  const handleSortClick = useCallback(
    (value) => {
      setSortBy(value);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set("sortBy", value);
        else next.delete("sortBy");
        return next;
      });
    },
    [setSearchParams],
  );

  const handlePendingFilterChange = useCallback((key, value) => {
    setPendingFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleClearFilters = useCallback(() => {
    const cleared = { minPrice: "", maxPrice: "", brand: "", minRating: "" };
    setPendingFilters(cleared);
    setAdvancedFilters(cleared);
  }, []);

  const handleResetFilters = useCallback(() => {
    handleClearFilters();
    setSortBy("");
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("sortBy");
      return next;
    });
  }, [handleClearFilters, setSearchParams]);

  const applyFilters = useCallback(() => {
    setAdvancedFilters(pendingFilters);
  }, [pendingFilters]);

  const handleRemoveFilter = useCallback((key) => {
    setAdvancedFilters((prev) => ({ ...prev, [key]: "" }));
    setPendingFilters((prev) => ({ ...prev, [key]: "" }));
  }, []);

  const activeFiltersCount = useMemo(
    () => Object.values(advancedFilters).filter((val) => val !== "").length,
    [advancedFilters],
  );

  const fitIntentParams = useMemo(
    () =>
      buildSearchFitIntent({
        query,
        category: categoryParam,
        categoryId: categoryIdParam,
        aiSession,
        user,
      }),
    [query, categoryParam, categoryIdParam, aiSession, user],
  );

  // A text query is not the home-profile fit. Hide that badge here so a
  // search for "phone" does not look scored against a different search.
  const showMatchScores =
    !query.trim() && shouldShowGridMatchScores(fitIntentParams);

  const { getMatchScore } = useProductGridMatchScores({
    products,
    enabled: showMatchScores,
  });

  const productContainerClass = useMemo(
    () => (viewMode === "grid" ? styles.productGrid : styles.productList),
    [viewMode],
  );

  const skeletonContainerClass = useMemo(
    () => (viewMode === "grid" ? styles.productGrid : styles.productCardList),
    [viewMode],
  );

  return (
    <div className="pageContainer">
      <PageSeo
        title={query ? `Search results for "${query}"` : "Search"}
        description="Search Simpary for products and marketplace listings."
        pathname="/search"
        noIndex
      />
      <div className="innerContainer">
        <div className={styles.searchResultsContainer}>
          <div className={styles.searchHeader}>
            <div>
              <h2>
                {query ? `Search results for "${query}"` : "Search"}
                {total > 0 && (
                  <span className={styles.resultCount}>
                    {" "}
                    ({total} {total === 1 ? "result" : "results"})
                  </span>
                )}
              </h2>
              {activeFiltersCount > 0 && (
                <p className={styles.activeFiltersInfo}>
                  {activeFiltersCount} filter
                  {activeFiltersCount !== 1 ? "s" : ""} applied
                </p>
              )}
            </div>
          </div>

          {loading && products.length === 0 ? (
            <div className={skeletonContainerClass}>
              {Array.from({ length: 8 }).map((_, i) => (
                <ProductSkeleton key={i} view={viewMode} />
              ))}
            </div>
          ) : loadError && products.length === 0 ? (
            <div className={styles.noResults} role="alert">
              <p>{loadError}</p>
              <button
                className={styles.filterToggleBtn}
                onClick={() => fetchSearchResults(true)}
              >
                Retry
              </button>
            </div>
          ) : products.length === 0 ? (
            <div className={styles.noResults}>
              <p>
                {query
                  ? `No products found for "${query}"`
                  : "Start typing to see results"}
              </p>
              <p className={styles.noResultsSubtext}>
                {query
                  ? "Try adjusting your search terms or browse our categories"
                  : "Try searching for a product or browse our categories"}
              </p>
            </div>
          ) : (
            <>
              <div className={styles.filtersSection}>
                <button
                  className={styles.filterToggleBtn}
                  onClick={() => {
                    if (!showAdvancedFilters) {
                      setPendingFilters(advancedFilters);
                    }
                    setShowAdvancedFilters(!showAdvancedFilters);
                  }}
                >
                  <FaFilter /> Advanced Filters
                  {activeFiltersCount > 0 && (
                    <span className={styles.filterBadge}>
                      {activeFiltersCount}
                    </span>
                  )}
                </button>

                {activeFiltersCount > 0 && (
                  <div className={styles.activeFilters}>
                    {advancedFilters.minPrice && (
                      <span className={styles.filterChip}>
                        Min Price: $
                        {Number(advancedFilters.minPrice).toLocaleString()}
                        <button
                          onClick={() => handleRemoveFilter("minPrice")}
                          className={styles.chipRemove}
                        >
                          <FaTimes />
                        </button>
                      </span>
                    )}
                    {advancedFilters.maxPrice && (
                      <span className={styles.filterChip}>
                        Max Price: $
                        {Number(advancedFilters.maxPrice).toLocaleString()}
                        <button
                          onClick={() => handleRemoveFilter("maxPrice")}
                          className={styles.chipRemove}
                        >
                          <FaTimes />
                        </button>
                      </span>
                    )}
                    {advancedFilters.brand && (
                      <span className={styles.filterChip}>
                        Brand: {advancedFilters.brand}
                        <button
                          onClick={() => handleRemoveFilter("brand")}
                          className={styles.chipRemove}
                        >
                          <FaTimes />
                        </button>
                      </span>
                    )}
                    {advancedFilters.minRating && (
                      <span className={styles.filterChip}>
                        <FaStar /> Min Rating: {advancedFilters.minRating}
                        <button
                          onClick={() => handleRemoveFilter("minRating")}
                          className={styles.chipRemove}
                        >
                          <FaTimes />
                        </button>
                      </span>
                    )}
                    <button
                      className={styles.clearAllFiltersBtn}
                      onClick={handleClearFilters}
                    >
                      Clear All
                    </button>
                  </div>
                )}

                {showAdvancedFilters && (
                  <div className={styles.advancedFiltersPanel}>
                    <div className={styles.filtersHeader}>
                      <h3>Filter Products</h3>
                      <button
                        className={styles.closeFiltersBtn}
                        onClick={() => setShowAdvancedFilters(false)}
                      >
                        <FaTimes />
                      </button>
                    </div>
                    <div className={styles.filtersContent}>
                      <div className={styles.filterGroup}>
                        <label className={styles.filterLabel}>
                          Price Range
                        </label>
                        <div className={styles.priceInputs}>
                          <input
                            type="number"
                            placeholder="Min Price"
                            value={pendingFilters.minPrice}
                            onChange={(e) =>
                              handlePendingFilterChange(
                                "minPrice",
                                e.target.value,
                              )
                            }
                            className={styles.priceInput}
                            min="0"
                          />
                          <span className={styles.priceSeparator}>-</span>
                          <input
                            type="number"
                            placeholder="Max Price"
                            value={pendingFilters.maxPrice}
                            onChange={(e) =>
                              handlePendingFilterChange(
                                "maxPrice",
                                e.target.value,
                              )
                            }
                            className={styles.priceInput}
                            min="0"
                            max={maxPrice}
                          />
                        </div>
                        <div className={styles.priceHint}>
                          Max: ${maxPrice.toLocaleString()}
                        </div>
                      </div>

                      <div className={styles.filterGroup}>
                        <label className={styles.filterLabel}>Brand</label>
                        <select
                          value={pendingFilters.brand}
                          onChange={(e) =>
                            handlePendingFilterChange("brand", e.target.value)
                          }
                          className={styles.filterSelect}
                        >
                          <option value="">All Brands</option>
                          {brands.map((brand) => (
                            <option key={brand._id} value={brand.name}>
                              {brand.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className={styles.filterGroup}>
                        <label className={styles.filterLabel}>
                          Minimum Rating
                        </label>
                        <select
                          value={pendingFilters.minRating}
                          onChange={(e) =>
                            handlePendingFilterChange(
                              "minRating",
                              e.target.value,
                            )
                          }
                          className={styles.filterSelect}
                        >
                          <option value="">Any Rating</option>
                          <option value="4">4+ Stars</option>
                          <option value="3">3+ Stars</option>
                          <option value="2">2+ Stars</option>
                          <option value="1">1+ Stars</option>
                        </select>
                      </div>
                    </div>
                    <div className={styles.filterActions}>
                      <button
                        type="button"
                        className={styles.applyFiltersBtn}
                        onClick={applyFilters}
                      >
                        Apply Filters
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className={styles.toolbar}>
                <div className={styles.viewToggle}>
                  <button
                    type="button"
                    onClick={() => setViewMode("grid")}
                    className={
                      viewMode === "grid"
                        ? styles.toggleActive
                        : styles.toggleButton
                    }
                    aria-label="Grid view"
                    title="Grid view"
                  >
                    <FaThLarge />
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode("list")}
                    className={
                      viewMode === "list"
                        ? styles.toggleActive
                        : styles.toggleButton
                    }
                    aria-label="List view"
                    title="List view"
                  >
                    <FaList />
                  </button>
                </div>

                <div className={styles.sorting}>
                  <div className={styles.sortChipRow}>
                    {SORT_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`buttonSort ${sortBy === option.value ? "active" : ""}`}
                        onClick={() => handleSortClick(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  <label className={styles.mobileSortWrap}>
                    <span className={styles.mobileSortLabel}>Sort</span>
                    <select
                      className={styles.mobileSortSelect}
                      aria-label="Sort products"
                      value={sortBy || ""}
                      onChange={(event) => handleSortClick(event.target.value)}
                    >
                      <option value="">Default</option>
                      {SORT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.text}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    className={`buttonReset ${styles.resetButton}`}
                    onClick={handleResetFilters}
                    type="button"
                  >
                    Reset Filters ↺
                  </button>
                </div>
              </div>

              <div className={productContainerClass}>
                {products.map((product) => {
                  const categoryId =
                    product.category?._id || product.category || "unknown";
                  return (
                    <ProductCard
                      key={product._id}
                      categoryId={categoryId}
                      product={product}
                      view={viewMode}
                      showMatchScore={showMatchScores}
                      precomputedMatchScore={getMatchScore(product._id)}
                    />
                  );
                })}
              </div>

              {hasMore && (
                <div className="buttonShowMoreContainer">
                  <button onClick={handleShowMore} className="buttonShowMore">
                    Show More
                  </button>
                </div>
              )}
              <AppFeedbackPrompt
                source="search"
                prompt="How are search results working for you?"
                metadata={{
                  query,
                  resultCount: total,
                  sessionId: readHomeChatMatchSessionId(),
                }}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchResultsPage;
